import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Bell, Ruler, Check, X, AlertCircle, User,
  MessageSquare, RefreshCw, ShoppingCart, Truck
} from 'lucide-react'
import {
  getUnreadCount,
  getNotifications,
  markAsRead,
  markSingleAsRead,
  getNotificationLink,
  NOTIFICATION_TYPE_COLORS
} from '../api/notifications'

// Icon mapping
const NOTIFICATION_ICONS = {
  dimension_request_created: Ruler,
  dimension_request_approved: Check,
  dimension_request_rejected: X,
  incident_created: AlertCircle,
  incident_assigned: User,
  incident_message: MessageSquare,
  incident_status_changed: RefreshCw,
  order_created: ShoppingCart,
  order_status_changed: Truck
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch unread count (polling every 30 seconds)
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000
  })

  // Fetch recent notifications when dropdown is open
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => getNotifications({ page: 1, perPage: 10 }),
    enabled: isOpen,
    staleTime: 10000
  })

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: (notificationId) => markSingleAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
    }
  })

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: () => markAsRead(null, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
    }
  })

  // Auto-mark all as read 3 seconds after opening
  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => {
      markAllReadMutation.mutate()
    }, 3000)
    return () => clearTimeout(timer)
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const unreadCount = unreadData?.count || 0
  const notifications = notificationsData?.items || []

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id)
    }

    // Navigate to entity
    const link = getNotificationLink(notification)
    if (link) {
      navigate(link)
    }

    setIsOpen(false)
  }

  const formatTime = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now - date

    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000)
      return minutes <= 1 ? 'только что' : `${minutes} мин назад`
    }

    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000)
      return `${hours} ч назад`
    }

    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000)
      return `${days} дн назад`
    }

    // Otherwise show date
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit'
    })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
        aria-label="Уведомления"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Уведомления</h3>
            {unreadCount > 0 && (
              <span className="text-xs text-gray-400">прочтутся автоматически</span>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Загрузка...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>Нет уведомлений</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell
                const colorClass = NOTIFICATION_TYPE_COLORS[notification.type] || 'bg-gray-100 text-gray-600'

                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                      !notification.is_read ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${colorClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''} text-gray-900 truncate`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                          )}
                        </div>
                        {notification.message && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(notification.created_at)}
                          {notification.actor && (
                            <span className="ml-1">
                              от {notification.actor.username}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200">
              <button
                onClick={() => {
                  navigate('/notifications')
                  setIsOpen(false)
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-1"
              >
                Все уведомления
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
